/**
 * Machine page › Cost.
 *
 * The demo's `machineTab('costs')` (line 2219): four headline tiles, cost by
 * ledger category, cost by crew, and the non-preventive work orders with their
 * labour / parts / freight / lost-production split.
 *
 * Two horizontal bar sets are drawn with plain divs rather than an SVG chart.
 * `BarChart` is owned by a sibling lane (P3a) and this lane must not create it;
 * a token-coloured bar row is the honest primitive for a ranked total, not an
 * approximation of a chart that would look different.
 *
 * Needs two domains: `costs` for the ledger and `work-orders` for the crew
 * split and the table.
 *
 * UI layer.
 *
 * @see src/versions/v1/modules/fleet/MachineScene.transform.ts
 * @see docs/logic/fleet/machine-page.md
 */

import { useMemo } from 'react';
import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { unpackTable } from '../../shared/columnar';
import { fmt$, fmtk, groupNum } from '../../shared/oem-engine';
import { useOemData, type WorkOrder } from '../../shared/useOemData';
import { useSourceDrawer } from '../issues/useSourceDrawer';
import { TabLoader } from './TabLoader';
import { allInCalc, costPerHourCalc } from './MachineCostTab.transform';
import { machineCost, type CostCategoryRow } from './MachineScene.transform';

export interface MachineCostTabProps {
  machineId: string;
  /** Site display name, for the lost-production caption. */
  mineName: string;
  cls: string;
  model: string;
}

export function MachineCostTab({ machineId, mineName, cls, model }: MachineCostTabProps) {
  const orders = useOemData('work-orders');
  const costs = useOemData('costs');
  const { openDataset } = useSourceDrawer();

  const view = useMemo(() => {
    if (!orders.data || !costs.data) return null;
    return machineCost(
      machineId,
      { workOrders: unpackTable<WorkOrder>(orders.data.wos) },
      costs.data.cost_by_machine_cat,
    );
  }, [orders.data, costs.data, machineId]);

  return (
    <TabLoader
      loading={orders.loading || costs.loading || !view}
      error={orders.error ?? costs.error}
      what="Cost data"
      rows={8}
    >
      {view ? (
        <div className="@container space-y-6">
          <div className="grid grid-cols-1 gap-3 @lg:grid-cols-2 @4xl:grid-cols-4">
            <Tile
              label="All-in cost · 18 months"
              value={fmtk(view.total)}
              onOpen={() =>
                openDataset('ledger_m', { filter: { machine_id: machineId }, calc: allInCalc(view) })
              }
              detail={
                view.lostProductionRate != null
                  ? `Lost production at A$${groupNum(view.lostProductionRate)}/h (${mineName} ${cls})`
                  : 'No lost-production rate for this class at this site'
              }
            />
            <Tile
              label="Cost per operating hour"
              value={view.costPerOpHour != null ? `A$${view.costPerOpHour}` : '—'}
              onOpen={() =>
                openDataset('cpm', {
                  filter: { machine_id: machineId },
                  calc: costPerHourCalc(machineId) ?? undefined,
                })
              }
              unit={`vs A$${view.peerMean.toFixed(0)} ${model} fleet mean`}
              detail={view.verdict}
            />
            <Tile
              label="Reactive share"
              value={String(view.reactiveSharePct)}
              unit="% of cost from breakdowns"
              detail={`${view.breakdowns} breakdowns · ${view.preventive} PMs · ${view.late} late`}
            />
            <Tile
              label="Labour rate applied"
              value={`A$${view.labourRate}`}
              unit="per hour"
              detail={`Freight from ${view.dc}, ${view.freightDays} d road`}
            />
          </div>

          <div className="grid grid-cols-1 gap-6 @3xl:grid-cols-2">
            <BarList
              title="Cost by category"
              subtitle="18 months · ledger rows"
              rows={view.categories}
            />
            <BarList
              title="Cost by crew"
              subtitle="Direct + lost production, from the work order"
              rows={view.crews}
            />
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Unplanned and corrective work orders
            </h3>
            <Table>
              <THead>
                <TR>
                  <TH>WO</TH>
                  <TH>Type</TH>
                  <TH>Component</TH>
                  <TH>Crew</TH>
                  <TH className="text-right">Labour</TH>
                  <TH className="text-right">Parts</TH>
                  <TH className="text-right">Freight</TH>
                  <TH className="text-right">Lost prod.</TH>
                  <TH className="text-right">Total</TH>
                </TR>
              </THead>
              <TBody>
                {view.rows.map((row) => (
                  <TR key={row.key}>
                    <TD className="mono whitespace-nowrap">{row.order.wo_id}</TD>
                    <TD className="whitespace-nowrap">{row.order.wo_type}</TD>
                    <TD>{row.order.component}</TD>
                    <TD className="whitespace-nowrap">{row.order.crew}</TD>
                    <TD className="mono text-right">{groupNum(row.order.labour_cost_aud)}</TD>
                    <TD className="mono text-right">{groupNum(row.order.parts_cost_aud)}</TD>
                    <TD className="mono text-right">{groupNum(row.order.freight_cost_aud)}</TD>
                    <TD className="mono text-right">
                      {groupNum(row.order.downtime_cost_aud || 0)}
                    </TD>
                    <TD className="mono text-right font-semibold">{groupNum(row.allIn)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <p className="text-[11px] text-muted">
              Amounts in Australian dollars.{' '}
              {view.opHours != null && view.costPerOpHour != null
                ? `Cost per operating hour = ${fmt$(view.total)} ÷ ${groupNum(view.opHours)} operating hours.`
                : null}
            </p>
          </div>
        </div>
      ) : null}
    </TabLoader>
  );
}

function Tile({
  label,
  value,
  unit,
  detail,
  onOpen,
}: {
  label: string;
  value: string;
  unit?: string;
  detail: string;
  /** Makes the figure a source link — the demo's dotted `FIG(…)`. */
  onOpen?: () => void;
}) {
  return (
    <div className="rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg">
      <p className="text-xs uppercase tracking-wider text-muted">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
        {onOpen ? (
          <button
            type="button"
            onClick={onOpen}
            title="Open the rows behind this figure"
            className="rounded underline decoration-dotted decoration-1 underline-offset-4 hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
          >
            {value}
          </button>
        ) : (
          value
        )}
        {unit ? <span className="ml-1 text-xs font-normal text-muted">{unit}</span> : null}
      </p>
      <p className="mt-1 text-[11px] text-muted">{detail}</p>
    </div>
  );
}

function BarList({
  title,
  subtitle,
  rows,
}: {
  title: string;
  subtitle: string;
  rows: readonly CostCategoryRow[];
}) {
  return (
    <section className="rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg">
      <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
      <p className="mb-3 text-[11px] text-muted">{subtitle}</p>
      <ul className="space-y-2">
        {rows.map((row) => (
          <li key={row.category} className="space-y-1">
            <div className="flex items-baseline justify-between gap-3 text-xs">
              <span className="truncate text-zinc-900 dark:text-zinc-100">{row.category}</span>
              <span className="mono shrink-0 text-muted">{fmtk(row.amount)}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
              <div
                className="h-full rounded-full bg-[color-mix(in_srgb,var(--color-accent-600)_45%,transparent)]"
                style={{ width: `${row.share}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
